import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Footprints, MoonStar, Brain, Sprout, TrendingUp, TrendingDown, Minus, Sparkles } from 'lucide-react';
import api from '../../../services/api';
import LivingAvatar from '../../living/LivingAvatar';
import { PILLAR_WORLD } from '../../../lib/worldState';
import { stateLabelFor } from '../../../lib/worldScenes';
import { overallBand } from '../../../lib/livingAvatarStates';

/**
 * DashboardSummaryCard — the chat's "your wellness IQ" hero, in the LIVING WORLD
 * look: the composite avatar + four world cards (Move/Recover/Mind/Fuel) that
 * drill into each pillar's world. Matches the Living Home dashboard so the chat
 * and home feel like one product. Self-fetches /wellness/iq + the composite and
 * listens to `wellness:refresh` so it never goes stale after a check-in.
 */

const PILLAR_ICON = { movement: Footprints, recovery: MoonStar, mental: Brain, nutrition: Sprout };
const STATE_TEXT = { movement: '#D4FF3A', recovery: '#5BDCF5', mental: '#C08CFF', nutrition: '#FFAE5C' };
const PILLAR_ORDER = ['movement', 'recovery', 'nutrition', 'mental'];

const buildCompositeSrc = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    const token = localStorage.getItem('hpme_token');
    return `${process.env.REACT_APP_BACKEND_URL}${url}&auth=${token || ''}`;
  }
  return url;
};

const WorldCard = ({ pk, score, delta, onOpen }) => {
  const meta = PILLAR_WORLD[pk];
  const Icon = PILLAR_ICON[pk];
  const has = score !== null && score !== undefined;
  const TrendIcon = delta == null || delta === 0 ? Minus : delta > 0 ? TrendingUp : TrendingDown;
  const trendColor = delta > 0 ? '#4ade80' : delta < 0 ? '#f87171' : '#71717a';
  return (
    <button
      type="button"
      onClick={() => onOpen(pk)}
      data-testid={`world-card-${pk}`}
      className="p-2.5 rounded-2xl flex flex-col gap-1 backdrop-blur-xl bg-black/40 border shadow-[0_6px_20px_rgba(0,0,0,0.4)] transition-all duration-200 hover:scale-[1.04] active:scale-95 text-left"
      style={{ borderColor: `${meta.color}40` }}
    >
      <span className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
        <span className="text-[10px] font-bold uppercase tracking-wider text-white/70">{meta.label}</span>
      </span>
      <span className="flex items-baseline gap-1.5">
        <span className="font-secondary font-bold text-2xl leading-none text-white tabular-nums">{has ? Math.round(score) : '—'}</span>
        {has && delta != null && (
          <span className="text-[10px] font-bold flex items-center" style={{ color: trendColor }}>
            <TrendIcon className="w-3 h-3" />{delta !== 0 ? Math.abs(Math.round(delta)) : ''}
          </span>
        )}
      </span>
      <span className="text-[9.5px] font-bold uppercase tracking-widest leading-none" style={{ color: STATE_TEXT[pk] }}>
        {has ? stateLabelFor(pk, score) : 'Awaiting'}
      </span>
    </button>
  );
};

const DashboardSummaryCard = () => {
  const navigate = useNavigate();
  const [iq, setIq] = useState(null);
  const [compositeSrc, setCompositeSrc] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.getWellnessIQ();
      setIq(r.data || null);
    } catch { setIq(null); }
    finally { setLoaded(true); }
    try {
      const res = await api.getAvatarComposite();
      setCompositeSrc(res?.data?.url ? buildCompositeSrc(res.data.url) : null);
    } catch { setCompositeSrc(null); }
  }, []);

  useEffect(() => {
    load();
    const onRefresh = () => load();
    window.addEventListener('wellness:refresh', onRefresh);
    return () => window.removeEventListener('wellness:refresh', onRefresh);
  }, [load]);

  const overall = iq?.wellness_iq ?? null;
  const sub = iq?.sub_scores || {};
  const deltas = iq?.sub_score_deltas || {};
  const band = overallBand(overall);
  const overallColor = overall == null ? '#52525b' : overall >= 80 ? '#CCFF00' : overall >= 60 ? '#facc15' : '#fb923c';
  const isEmpty = loaded && overall == null;

  return (
    <div className="rounded-2xl border border-white/10 bg-zinc-900/40 backdrop-blur-md p-4" data-testid="card-dashboard-summary">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-primary" />
          <p className="text-[10px] text-primary uppercase tracking-wider font-secondary font-bold">Your Journey</p>
        </div>
        <Link to="/dashboard" className="text-[10px] text-primary hover:underline" data-testid="open-full-dashboard-link">
          Open full dashboard →
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row items-stretch gap-3">
        {/* Composite avatar — taps into the full living home */}
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="relative w-[150px] mx-auto sm:mx-0 shrink-0 rounded-2xl overflow-hidden border border-white/10 shadow-[0_10px_36px_rgba(0,0,0,0.5)] transition-transform hover:scale-[1.02]"
          data-testid="dash-tile-wellness-iq"
        >
          <LivingAvatar src={compositeSrc} />
          <div className="absolute inset-x-0 bottom-0 px-2.5 py-2 flex items-end justify-between">
            <span className="font-secondary font-black text-3xl leading-none tabular-nums" style={{ color: overallColor }}>
              {overall == null ? '—' : Math.round(overall)}
            </span>
            <span className="text-[8px] font-bold uppercase tracking-[0.2em] pb-1" style={{ color: band.color }}>
              {band.label}
            </span>
          </div>
        </button>

        {/* 4 world cards */}
        <div className="grid grid-cols-2 gap-2 flex-1 content-center">
          {PILLAR_ORDER.map((pk) => (
            <WorldCard
              key={pk}
              pk={pk}
              score={sub[pk]}
              delta={deltas[pk]}
              onOpen={(k) => navigate(`/world/${k}`)}
            />
          ))}
        </div>
      </div>

      {isEmpty && (
        <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
          Tap any world to dive in. They come alive as you log how you slept, what you ate, how you&rsquo;re feeling — or do a quick mobility check.
        </p>
      )}
    </div>
  );
};

export default DashboardSummaryCard;
